import { use } from 'react';
import type { LocaleId } from '@bannermatic/core';
import type { MarketingCopy } from './types';

export * from './types';

/** Each language is its own chunk: visitors load only the texts of the language they read. */
const LOADERS: Record<LocaleId, () => Promise<{ default: MarketingCopy }>> = {
  en: () => import('./en'),
  uk: () => import('./uk'),
  pl: () => import('./pl'),
  ro: () => import('./ro'),
  es: () => import('./es'),
  ru: () => import('./ru'),
};

type Tracked = Promise<MarketingCopy> & { status?: 'fulfilled'; value?: MarketingCopy };
const loading = new Map<LocaleId, Tracked>();

/** Loads the texts of a language (once). */
export function loadMarketingCopy(locale: LocaleId): Promise<MarketingCopy> {
  let promise = loading.get(locale);
  if (!promise) {
    promise = LOADERS[locale]().then((m) => {
      // React's use() reads these fields and returns loaded texts without suspending
      promise!.status = 'fulfilled';
      promise!.value = m.default;
      return m.default;
    }) as Tracked;
    loading.set(locale, promise);
  }
  return promise;
}

/** Texts of a language that must already be loaded (head tags, pre-rendering). */
export function loadedMarketingCopy(locale: LocaleId): MarketingCopy {
  const value = loading.get(locale)?.value;
  if (!value) throw new Error(`Marketing texts for "${locale}" are not loaded`);
  return value;
}

/** Hook: the texts of a language; suspends while they load. */
export function useMarketingCopy(locale: LocaleId): MarketingCopy {
  return use(loadMarketingCopy(locale));
}
